import { memo } from "react";
import { Row, Col } from "react-bootstrap";
import {
  FaBuilding,
  FaIndustry,
  FaGlobe,
  FaInfoCircle,
  FaLink,
  FaBullseye,
  FaEnvelope,
  FaPhone,
  FaLanguage,
  FaUser,
  FaDollarSign,
  FaChartLine,
  FaMapPin,
  FaMapMarkedAlt,
  FaBoxes,
  FaBoxOpen,
  FaTag,
  FaPercentage,
  FaIdBadge,
  FaUserCheck,
  FaTicketAlt,
} from "react-icons/fa";
import { User } from "../../types/Types";

interface BusinessProfileProps {
  userData: User;
}

const IMG_OBJECT_FIT_STYLE = { objectFit: "contain" as const };

const BusinessProfile = memo(function BusinessProfile({
  userData,
}: BusinessProfileProps) {
  return (
    <div>
      <div className="card shadow-lg border-0 mt-3">
        <div className="d-flex px-3 px-md-4 py-3 gradient-bg text-white align-items-center rounded-top">
          <h5 className="mb-0">Business Details</h5>
        </div>
        <div className="p-3">
        <Row>
          <Col md={4} className="mb-3 d-block d-md-none">
            <div className="d-flex align-items-start">
              <FaBuilding size={20} className="me-2 mt-1 text-secondary" />
              <div className="d-flex justify-content-between w-100">
                <div>
                  <strong>Business Name</strong>
                  <div className="text-muted small">
                    {userData.businessName || "Not Provided Yet"}
                  </div>
                </div>
                <img
                  src={userData.BusinessLogo}
                  className="rounded-circle me-2 mt-1"
                  width={40}
                  height={40}
                  style={IMG_OBJECT_FIT_STYLE}
                  alt="logo"
                />
              </div>
            </div>
          </Col>

          <Col md={4} className="mb-3">
            <div className="d-flex align-items-start">
              <FaIndustry size={20} className="me-2 mt-1 text-secondary" />
              <div>
                <strong>Sector / Industry</strong>
                <div className="text-muted small">
                  {userData.businessSectorIndustry || "Not Provided Yet"}
                </div>
              </div>
            </div>
          </Col>

          <Col md={4} className="mb-3">
            <div className="d-flex align-items-start">
              <FaGlobe size={20} className="me-2 mt-1 text-secondary" />
              <div>
                <strong>Countries of Operation</strong>
                <div className="text-muted small">
                  {userData.businessCountryOperation || "Not Provided Yet"}
                </div>
              </div>
            </div>
          </Col>

          <Col md={4} className="mb-3">
            <div className="d-flex align-items-start">
              <FaInfoCircle size={20} className="me-2 mt-1 text-secondary" />
              <div>
                <strong>About the Business</strong>
                <div className="text-muted small">
                  {userData.businessAbout || "Not Provided Yet"}
                </div>
              </div>
            </div>
          </Col>

          <Col md={4} className="mb-3">
            <div className="d-flex align-items-start">
              <FaLink
                size={20}
                className="me-2 mt-1 text-secondary flex-shrink-0"
              />
              <div className="d-flex flex-column w-100">
                <strong>Website URL</strong>
                <a
                  href={userData.businessWebsiteUrl || "#"}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-muted small text-truncate pr-1"
                >
                  {userData.businessWebsiteUrl || "Not Provided Yet"}
                </a>
              </div>
            </div>
          </Col>

          <Col md={4} className="mb-3">
            <div className="d-flex align-items-start">
              <FaBullseye size={20} className="me-2 mt-1 text-secondary" />
              <div>
                <strong>Objectives</strong>
                <div className="text-muted small">
                  {userData.businessObjectives || "Not Provided Yet"}
                </div>
              </div>
            </div>
          </Col>
        </Row>
        </div>
      </div>

      <div className="card shadow-lg border-0 mt-3">
        <div className="d-flex px-3 px-md-4 py-3 gradient-bg text-white align-items-center rounded-top">
          <h5 className="mb-0">About</h5>
        </div>
        <div className="p-3">
        <Row>
          <Col className="mb-3">
            <div className="text-muted small">
              {userData.personalAbout || "Not Provided Yet"}
            </div>
          </Col>
        </Row>
        </div>
      </div>

      <div className="card shadow-lg border-0 mt-3">
        <div className="d-flex px-3 px-md-4 py-3 gradient-bg text-white align-items-center rounded-top">
          <h5 className="mb-0">Personal Details</h5>
        </div>
        <div className="p-3">
        <Row>
          <Col md={4} className="mb-3">
            <div className="d-flex align-items-start">
              <FaEnvelope size={20} className="me-2 mt-1 text-secondary" />
              <div>
                <strong>Email</strong>
                <div className="text-muted small">
                  {userData.email || "Not Provided Yet"}
                </div>
              </div>
            </div>
          </Col>

          <Col md={4} className="mb-3">
            <div className="d-flex align-items-start">
              <FaPhone size={20} className="me-2 mt-1 text-secondary" />
              <div>
                <strong>Contact Number</strong>
                <div className="text-muted small">
                  {userData.businessTelephoneNumber || "Not Provided Yet"}
                </div>
              </div>
            </div>
          </Col>

          <Col md={4} className="mb-3">
            <div className="d-flex align-items-start">
              <FaLanguage size={20} className="me-2 mt-1 text-secondary" />
              <div>
                <strong>Language Preference</strong>
                <div className="text-muted small">
                  {userData.personalLanguagePreference || "Not Provided Yet"}
                </div>
              </div>
            </div>
          </Col>

          <Col md={4} className="mb-3">
            <div className="d-flex align-items-start">
              <FaUser size={20} className="me-2 mt-1 text-secondary" />
              <div>
                <strong>Gender</strong>
                <div className="text-muted small">
                  {userData.personalGender || "Not Provided Yet"}
                </div>
              </div>
            </div>
          </Col>

          <Col md={4} className="mb-3">
            <div className="d-flex align-items-start">
              <FaDollarSign size={20} className="me-2 mt-1 text-secondary" />
              <div>
                <strong>Currency</strong>
                <div className="text-muted small">
                  {userData.businessCurrency || "Not Provided Yet"}
                </div>
              </div>
            </div>
          </Col>

          <Col md={4} className="mb-3">
            <div className="d-flex align-items-start">
              <FaChartLine size={20} className="me-2 mt-1 text-secondary" />
              <div>
                <strong>Turnover</strong>
                <div className="text-muted small">
                  {userData.businessTurnover || "Not Provided Yet"}
                </div>
              </div>
            </div>
          </Col>

          <Col md={4} className="mb-3">
            <div className="d-flex align-items-start">
              <FaMapPin size={20} className="me-2 mt-1 text-secondary" />
              <div>
                <strong>County</strong>
                <div className="text-muted small">
                  {userData.county || "Not Provided Yet"}
                </div>
              </div>
            </div>
          </Col>

          <Col md={4} className="mb-3">
            <div className="d-flex align-items-start">
              <FaMapPin size={20} className="me-2 mt-1 text-secondary" />
              <div>
                <strong>Postal Code</strong>
                <div className="text-muted small">
                  {userData.businessPostCode || "Not Provided Yet"}
                </div>
              </div>
            </div>
          </Col>

          <Col md={4} className="mb-3">
            <div className="d-flex align-items-start">
              <FaMapMarkedAlt size={20} className="me-2 mt-1 text-secondary" />
              <div>
                <strong>Address Line One</strong>
                <div className="text-muted small">
                  {userData.businessAddressLineOne || "Not Provided Yet"}
                </div>
              </div>
            </div>
          </Col>

          <Col md={4} className="mb-3">
            <div className="d-flex align-items-start">
              <FaMapMarkedAlt size={20} className="me-2 mt-1 text-secondary" />
              <div>
                <strong>Address Line Two</strong>
                <div className="text-muted small">
                  {userData.businessAddressLineTwo || "Not Provided Yet"}
                </div>
              </div>
            </div>
          </Col>
        </Row>
        </div>
      </div>

      <div className="card shadow-lg border-0 mt-3">
        <div className="d-flex px-3 px-md-4 py-3 gradient-bg text-white align-items-center rounded-top">
          <h5 className="mb-0">Offerings</h5>
        </div>
        <div className="p-3">
        <Row>
          <Col md={4} className="mb-3">
            <div className="d-flex align-items-start">
              <FaBoxes size={20} className="me-2 mt-1 text-secondary" />
              <div>
                <strong>Product Services</strong>
                <div className="text-muted small">
                  {userData.businessProductServices || "Not Provided Yet"}
                </div>
              </div>
            </div>
          </Col>

          <Col md={4} className="mb-3">
            <div className="d-flex align-items-start">
              <FaBoxOpen size={20} className="me-2 mt-1 text-secondary" />
              <div>
                <strong>Product Interested</strong>
                <div className="text-muted small">
                  {userData.productInterested || "Not Provided Yet"}
                </div>
              </div>
            </div>
          </Col>

          <Col md={4} className="mb-3">
            <div className="d-flex align-items-start">
              <FaTag size={20} className="me-2 mt-1 text-secondary" />
              <div>
                <strong>Discount Code</strong>
                <div className="text-muted small">
                  {userData.businessDiscountCode || "Not Provided Yet"}
                </div>
              </div>
            </div>
          </Col>

          <Col md={4} className="mb-3">
            <div className="d-flex align-items-start">
              <FaPercentage size={20} className="me-2 mt-1 text-secondary" />
              <div>
                <strong>Discount</strong>
                <div className="text-muted small">
                  {userData.businessDiscount || "Not Provided Yet"}
                </div>
              </div>
            </div>
          </Col>
        </Row>
        </div>
      </div>
      {/* Membership Details */}
      <div className="card shadow-lg border-0 mt-3">
        <div className="d-flex px-3 px-md-4 py-3 gradient-bg text-white align-items-center rounded-top">
          <h5 className="mb-0">Membership Details</h5>
        </div>
        <div className="p-3">
        <Row>
          <Col md={4} className="mb-3">
            <div className="d-flex align-items-start">
              <FaIdBadge size={20} className="me-2 mt-1 text-secondary" />
              <div>
                <strong>Membership Name</strong>
                <div className="text-muted small">
                  {userData.membershipName || "Not Provided Yet"}
                </div>
              </div>
            </div>
          </Col>
          <Col md={4} className="mb-3">
            <div className="d-flex align-items-start">
              <FaUserCheck size={20} className="me-2 mt-1 text-secondary" />
              <div>
                <strong>Membership Type</strong>
                <div className="text-muted small">
                  {userData.isAnnualMembership ? "Annual" : "Monthly"}
                </div>
              </div>
            </div>
          </Col>
          <Col md={4} className="mb-3">
            <div className="d-flex align-items-start">
              <FaTicketAlt size={20} className="me-2 mt-1 text-secondary" />
              <div>
                <strong>Referral Code</strong>
                <div className="text-muted small">
                  {userData.referralCode || "Not Provided Yet"}
                </div>
              </div>
            </div>
          </Col>
          {userData.referralCodeUsed && (
            <Col md={4} className="mb-3">
              <div className="d-flex align-items-start">
                <FaTicketAlt size={20} className="me-2 mt-1 text-secondary" />
                <div>
                  <strong>Referral Code Used</strong>
                  <div className="text-muted small">
                    {userData.referralCodeUsed || "Not Provided Yet"}
                  </div>
                </div>
              </div>
            </Col>
          )}
        </Row>
        </div>
      </div>
    </div>
  );
});

BusinessProfile.displayName = "BusinessProfile";

export default BusinessProfile;
